import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, QuoteIcon, BadgeCheckIcon, UsersIcon, GlobeIcon } from 'lucide-react';
type Testimonial = {
  name: string;
  role: string;
  country: string;
  flag: string;
  quote: string;
  highlight: string;
  initials: string;
  avatarBg: string;
  avatarText: string;
};
const testimonials: Testimonial[] = [{
  name: 'Carlos M.',
  role: 'Profesor de natación',
  country: 'México',
  flag: '🇲🇽',
  quote: 'En 5 minutos preparo sesiones que antes me llevaban horas. Los alumnos avanzan más rápido.',
  highlight: '¡Recuperé mis domingos!',
  initials: 'CM',
  avatarBg: 'bg-blue-500/15 border-blue-500/30',
  avatarText: 'text-blue-400'
}, {
  name: 'Laura S.',
  role: 'Entrenadora privada',
  country: 'España',
  flag: '🇪🇸',
  quote: 'El módulo Business Pro me ayudó a organizar mis clases particulares y duplicar mis ingresos en dos meses.',
  highlight: 'Dupliqué mis ingresos',
  initials: 'LS',
  avatarBg: 'bg-purple-500/15 border-purple-500/30',
  avatarText: 'text-purple-400'
}, {
  name: 'Ricardo G.',
  role: 'Coordinador de academia',
  country: 'Argentina',
  flag: '🇦🇷',
  quote: 'Ahora puedo mostrarle a los padres exactamente en qué nivel está cada niño con datos reales.',
  highlight: 'Las evaluaciones son geniales',
  initials: 'RG',
  avatarBg: 'bg-emerald-500/15 border-emerald-500/30',
  avatarText: 'text-emerald-400'
}];
const stats = [{
  Icon: StarIcon,
  value: '4.9/5',
  label: 'Calificación promedio'
}, {
  Icon: UsersIcon,
  value: '+850',
  label: 'Reseñas verificadas'
}, {
  Icon: GlobeIcon,
  value: '+15',
  label: 'Países'
}];
function Stars() {
  return <div className="flex items-center gap-0.5" aria-label="5 de 5 estrellas" data-id="element-181">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-secondary fill-secondary drop-shadow-[0_0_4px_rgba(245,158,11,0.5)]" data-id="element-182" />)}
    </div>;
}
export function ExpertSection() {
  return <section className="w-full bg-bg-dark py-24 px-4 border-t border-border-light/50 relative overflow-hidden" data-id="element-183">
      {/* Decorative blobs */}
      <div aria-hidden className="absolute top-1/4 -left-20 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl pointer-events-none" data-id="element-184" />
      <div aria-hidden className="absolute bottom-1/4 -right-20 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-185" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-186">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12" data-id="element-187">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary/10 border border-secondary/20 mb-6" data-id="element-188">
            <BadgeCheckIcon className="w-4 h-4 text-secondary" data-id="element-189" />
            <span className="text-sm font-semibold text-secondary tracking-wide uppercase" data-id="element-190">
              Testimonios verificados
            </span>
          </div>

          <div className="flex items-center justify-center gap-2 mb-5" data-id="element-191">
            <Stars data-id="element-192" />
            <span className="text-text-main font-bold text-base" data-id="element-193">4.9</span>
            <span className="text-text-sec text-sm" data-id="element-194">· 850+ reseñas</span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-4 tracking-tight" data-id="element-195">
            Lo que dicen los profesores
            <br className="hidden sm:block" data-id="element-196" />
            <span className="bg-gradient-to-r from-secondary to-yellow-400 bg-clip-text text-transparent" data-id="element-197">
              {' '}
              de todo el mundo
            </span>
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto" data-id="element-198">
            Más de 850 docentes ya transformaron sus clases con el MegaPack.
          </p>
        </motion.div>

        {/* Stats row */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto mb-14" data-id="element-199">
          {stats.map(({
          Icon,
          value,
          label
        }, i) => <div key={i} className="bg-bg-alt border border-border-light rounded-2xl px-4 py-5 sm:py-6 text-center" data-id="element-200">
              <Icon className="w-5 h-5 text-secondary mx-auto mb-2" data-id="element-201" />
              <div className="text-2xl sm:text-3xl font-black text-text-main mb-0.5" data-id="element-202">
                {value}
              </div>
              <div className="text-text-sec text-[11px] sm:text-xs font-medium uppercase tracking-wide" data-id="element-203">
                {label}
              </div>
            </div>)}
        </motion.div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6" data-id="element-204">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} whileHover={{
          y: -6
        }} className="group relative bg-bg-alt border border-border-light hover:border-secondary/40 rounded-2xl p-6 sm:p-7 text-left transition-all duration-300 hover:shadow-2xl hover:shadow-secondary/10 overflow-hidden flex flex-col" data-id="element-205">
              {/* Subtle hover glow */}
              <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-secondary/10 opacity-0 group-hover:opacity-100 blur-3xl transition-opacity duration-500 pointer-events-none" data-id="element-206" />

              {/* Quote mark */}
              <QuoteIcon className="absolute top-5 right-5 w-8 h-8 text-secondary/15" strokeWidth={2} data-id="element-207" />

              <div className="relative z-10 flex flex-col flex-1" data-id="element-208">
                <Stars data-id="element-209" />

                <p className="text-text-main font-bold text-base sm:text-lg leading-snug mt-4 mb-2" data-id="element-210">
                  "{t.highlight}"
                </p>
                <p className="text-text-sec text-sm sm:text-base leading-relaxed mb-6" data-id="element-211">
                  {t.quote}
                </p>

                {/* Author footer */}
                <div className="flex items-center gap-3 mt-auto pt-5 border-t border-border-light" data-id="element-212">
                  <div className={`w-11 h-11 rounded-full border ${t.avatarBg} ${t.avatarText} flex items-center justify-center font-black text-sm flex-shrink-0`} data-id="element-213">
                    {t.initials}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-214">
                    <div className="flex items-center gap-1.5" data-id="element-215">
                      <p className="text-text-main font-bold text-sm truncate" data-id="element-216">
                        {t.name}
                      </p>
                      <BadgeCheckIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" aria-label="Verificado" data-id="element-217" />
                    </div>
                    <p className="text-text-sec text-xs truncate flex items-center gap-1" data-id="element-218">
                      <span data-id="element-219">{t.role}</span>
                      <span className="text-border-light" data-id="element-220">·</span>
                      <span data-id="element-221">
                        {t.flag} {t.country}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Footer reassurance */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="text-center text-text-sec text-sm mt-10 flex items-center justify-center gap-2 flex-wrap" data-id="element-222">
          <BadgeCheckIcon className="w-4 h-4 text-primary" data-id="element-223" />
          <span data-id="element-224">Reseñas verificadas por compradores reales</span>
        </motion.p>
      </div>
    </section>;
}